import React, { useState, useEffect } from "react";
import {
  DropDownList,
  type DropDownListChangeEvent,
  type DropDownListFilterChangeEvent,
} from "@progress/kendo-react-dropdowns";
import {
  MaskedTextBox,
  type MaskedTextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import countries from "../../../../data/data/countryList.json";
import phoneFormats from "../../../../data/data/phoneFormat.json";
import { COUNTRY_BASE_PATH, ICON_BASE_PATH } from "@/app/utils/config";
import { t } from "i18next";
import "./Mobilefiled.css";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/redux/store";

interface CountryJson {
  name?: {
    common?: string;
  };
  flags?: {
    svg?: string;
  };
  idd?: {
    root?: string;
    suffixes?: string[];
  };
  cca2?: string;
}

interface Country {
  name: string;
  flag: string;
  dialCode: string;
  mask: string | undefined;
  cca2: string;
}

interface CountryValueRenderProps {
  country: Country;
}

interface MobilefiledProps {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onValidate?: (field: string, isValid: boolean) => void;
  onCountryCodeChange?: (dialCode: string) => void;
  disable?: boolean;
  triggerValidation?: boolean;
  countryDialCode: string;
  fieldName?: string;
  mobileError?: string;
  starlable?: boolean;
  otherFieldHasValue?: boolean;
  showStarLabel?: boolean; // fixed
}

// ............................... extractDialCode ................................
const extractDialCode = (country: CountryJson): string => {
  if (!country.idd?.root) return "";
  const root = country.idd.root.trim();
  const suffix = country.idd.suffixes?.[0] || "";
  return `${root}${suffix}`;
};

// ............................... getMaskForCountry ................................
const getMaskForCountry = (country: CountryJson): string => {
  const commonName = country?.name?.common;
  switch (commonName) {
    case "United Arab Emirates":
      return "(00) 000-0000";
    case "India":
      return "00000-00000";
    case "United States":
      return "(000) 000-0000";
    case "Spain":
      return "000 00 00 00";
    default:
      return "000000000000000";
  }
};

// ............................... transformedcountries ................................
const transformedcountries: Country[] = (countries as CountryJson[]).map(
  (c) => ({
    name: c.name?.common || "",
    flag: c.flags?.svg || "",
    dialCode: extractDialCode(c),
    mask: getMaskForCountry(c),
    cca2: c.cca2 || "",
  }),
);

// ............................... getDefaultCountry ................................
const getDefaultCountry = (userCountryCode2?: string): Country => {
  if (userCountryCode2) {
    const userCountry = transformedcountries.find(
      (c) => c.cca2 === userCountryCode2.toUpperCase()
    );
    if (userCountry) return userCountry;
  }

  const defaultCountry = transformedcountries.find(
    (c) => c.dialCode === "+971"
  );

  return defaultCountry || transformedcountries[0];
};

const CountryValueRender: React.FC<CountryValueRenderProps> = ({ country }) => (
  <span className="dropdown-value-render">
    <img
      src={`${COUNTRY_BASE_PATH}${country.flag}`}
      alt={country.name}
      className="flag-icon"
    />
    <span className="font-bold!">{country.dialCode}</span>
    <img
      src={`${ICON_BASE_PATH}down_arrow1.svg`}
      className="gx-mail-arrow"
      alt="mail"
    />
  </span>
);

const renderCountryValue = (_el: React.ReactElement, val: Country) => (
  <CountryValueRender country={val} />
);

// ............................... Mobilefiled ................................
const Mobilefiled: React.FC<MobilefiledProps> = ({
  id,
  label,
  value,
  onChange,
  onValidate,
  onCountryCodeChange,
  disable = false,
  triggerValidation,
  countryDialCode,
  fieldName = "mobileFiled",
  mobileError,
  starlable = false,
  otherFieldHasValue = false,
}) => {
  const userData = useSelector((state: RootState) => state.userData.data);
  

  const [selectedCountry, setSelectedCountry] = useState<Country>(() => {
    if (countryDialCode) {
      const match = transformedcountries.find(
        (c) => c.dialCode === countryDialCode,
      );
      if (match) return match;
    }

    return getDefaultCountry(userData?.countryCode2);
  });

  const [phoneLength, setPhoneLength] = useState<number>(9);
  const [phoneMask, setPhoneMask] = useState<string>("000000000");
  const [touched, setTouched] = useState(false);
  const [localValidationMessage, setLocalValidationMessage] = useState("");
  const [filteredCountries, setFilteredCountries] =
    useState<Country[]>(transformedcountries);
  const [hasManuallyChanged, setHasManuallyChanged] = useState(false);

  // ............................... validatePhoneNumber ................................
  const validatePhoneNumber = (val: string): string => {
    let msg = "";
    const digitsOnly = val.replaceAll(/\D/g, "");

    if (starlable) {
      if (!digitsOnly.length) {
        msg = `${t("please_enter_the")} ${label}.`;
        return msg;
      }
    } else if (!digitsOnly.length) {
      if (otherFieldHasValue) {
        return "";
      } else {
        msg = `${t("please_enter_the")} ${label}.`;
        return msg;
      }
    }

    if (digitsOnly.length > 0) {
      if (digitsOnly.length !== phoneLength) {
        msg = `${t("enter_valid_mob_no")}.`;
      }
    }

    return msg;
  };

  // ............................... applyPhoneFormat ................................
  const applyPhoneFormat = (digits: string, mask: string): string => {
    let result = "";
    let digitIndex = 0;
    for (const element of mask) {
      if (element === "0") {
        result += digits[digitIndex] ?? "";
        digitIndex++;
      } else {
        result += element;
      }
      if (digitIndex >= digits.length) break;
    }
    return result;
  };

  // ............................... handleInputChange ................................
  const handleInputChange = (e: MaskedTextBoxChangeEvent) => {
    const newValue = e.value ?? "";
    const digitsOnly = newValue.replaceAll(/\D/g, "");
    if (digitsOnly.length <= phoneLength) {
      const formattedValue = applyPhoneFormat(digitsOnly, phoneMask);
      onChange(formattedValue);

      if (touched) {
        const msg = validatePhoneNumber(formattedValue);
        setLocalValidationMessage(msg);
        onValidate?.(fieldName, !msg);
      }
    }
  };

  // ............................... handleCountryChange ................................
  const handleCountryChange = (e: DropDownListChangeEvent) => {
    const newCountry = e.value as Country;
    setSelectedCountry(newCountry);
    setFilteredCountries(transformedcountries);
    onChange("");
    setTouched(false);
    setLocalValidationMessage("");
    setHasManuallyChanged(true);
    onCountryCodeChange?.(newCountry.dialCode);
  };

  // ............................... handleFilterChange ................................
  const handleFilterChange = (event: DropDownListFilterChangeEvent) => {
    const search = event.filter.value?.toLowerCase() || "";
    const filtered = transformedcountries.filter(
      (c) =>
        c.name.toLowerCase().includes(search) || c.dialCode.includes(search),
    );
    setFilteredCountries(filtered);
  };

  // ............................... handleBlur ................................
  const handleBlur = () => {
    setTouched(true);
    const msg = validatePhoneNumber(value);
    setLocalValidationMessage(msg);
    onValidate?.(fieldName, !msg);
  };

  // ............................... handleFocus ................................
  const handleFocus = () => {};

  // ............................... useEffect: phone format on country change ................................
  useEffect(() => {
    const matched = phoneFormats.find((f) => f.label === selectedCountry.name);
    if (matched) {
      const phoneLength = matched.phoneLength as number;
      setPhoneLength(phoneLength);
      const mask = matched.phoneFormat.replaceAll("X", "0");
      setPhoneMask(mask);
    } else {
      setPhoneLength(9);
      setPhoneMask("000000000");
    }
  }, [selectedCountry]);

  // ............................... useEffect: sync selectedCountry when countryDialCode prop changes ................................
  useEffect(() => {
    if (countryDialCode) {
      const match = transformedcountries.find(
        (c) => c.dialCode === countryDialCode,
      );
      if (match && match.dialCode !== selectedCountry.dialCode) {
        setSelectedCountry(match);
      }
    }
  }, [countryDialCode, selectedCountry.dialCode]);

  // ............................... useEffect: sync selectedCountry from userData when no countryDialCode ................................
  useEffect(() => {
    if (!countryDialCode && !hasManuallyChanged && userData?.countryCode2) {
      const userCountry = transformedcountries.find(
        (c) => c.cca2 === userData.countryCode2.toUpperCase(),
      );

      if (userCountry && userCountry.dialCode !== selectedCountry.dialCode) {
        setSelectedCountry(userCountry);
      }
    }
  }, [
    userData?.countryCode2,
    countryDialCode,
    hasManuallyChanged,
    selectedCountry.dialCode,
  ]);

  // ............................... useEffect: fire onCountryCodeChange on mount ................................
  useEffect(() => {
    if (!countryDialCode && onCountryCodeChange) {
      onCountryCodeChange(selectedCountry.dialCode);
    }
  }, []);

  // ............................... useEffect: mobileError sets touched ................................
  useEffect(() => {
    if (mobileError) {
      setTouched(true);
    }
  }, [mobileError]);

  
  useEffect(() => {
    if (triggerValidation) {
      const msg = validatePhoneNumber(value);
      setLocalValidationMessage(msg);
      onValidate?.(fieldName, !msg);

      
      if (msg) {
        setTouched(true);
      }
    } else {
      
      setTouched(false);
      setLocalValidationMessage("");
    }
  }, [triggerValidation]);

  // ............................... useEffect: re-validate on optional-field config changes ................................
  useEffect(() => {
    if (touched || triggerValidation) {
      const msg = validatePhoneNumber(value);
      setLocalValidationMessage(msg);
      onValidate?.(fieldName, !msg);
    }
  }, [otherFieldHasValue, starlable]);

  
  const isInvalid = (!!localValidationMessage || !!mobileError) && touched;

  const displayError = mobileError || localValidationMessage;

  const showRequiredStar = starlable || !otherFieldHasValue;

  return (
    <div className="gx_input_container mobile-filed" id={id}>
      <label
        className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
      >
        {label}
        {showRequiredStar && (
          <span
            className={`gx_input_star ${
              value ? "gx_input_star_float" : ""
            }`}
          >
            *
          </span>
        )}
      </label>

      <div className={`gx_input_cont ${isInvalid ? "invalid" : "valid"}`}>
        <DropDownList
          data={filteredCountries}
          value={selectedCountry}
          onChange={handleCountryChange}
          textField="name"
          className="mobilecssselect"
          popupSettings={{ className: "flag-dropdown-popup" }}
          filterable={true}
          onFilterChange={handleFilterChange}
          disabled={disable}
          valueRender={renderCountryValue}
          itemRender={(li, itemProps) => {
            const item = itemProps.dataItem as Country;
            return React.cloneElement(
              li,
              li.props,
              <span className="dropdown-item-render">
                <img
                  src={`${COUNTRY_BASE_PATH}${item.flag}`}
                  alt={item.name}
                  className="flag-icon"
                />
                <span className="dial-code">{item.dialCode}</span>
                <span className="country-name">{item.name}</span>
              </span>,
            );
          }}
        />

        <div className="custom-divider"></div>

        <MaskedTextBox
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          mask={phoneMask}
          className="gx-textbox-css"
          disabled={disable}
        />
      </div>

      {isInvalid && displayError && (
        <div className="gx_input_validationmessage gx-font-regular">
          {displayError}
        </div>
      )}
    </div>
  );
};

export default Mobilefiled;
